import { InvoiceStatus, InvoiceType, RevenueOperationType, TransactionType } from '../swagger/api';

export type InvoiceStatusContext = 'business-invoice' | 'business-card' | 'private';

export type InvoiceStatusVariant =
  | 'default'
  | 'overdue'
  | 'creditBalance'
  | 'deposited'
  | 'missingPayment'
  | 'paidOut';

export interface InvoiceStatusDisplayInfo {
  cmsKey: string;
  variant: InvoiceStatusVariant;
  showBadge: boolean;
  rowClassName: string;
}

const PENDING_STATUSES = [InvoiceStatus.Pending] as const;
const COLLECTION_STATUSES = [
  InvoiceStatus.PendingExternalDebtCollection,
  InvoiceStatus.SentToExternalDebtCollection
] as const;
const PAID_STATUSES = [
  InvoiceStatus.Paid,
  InvoiceStatus.Settled,
  InvoiceStatus.PaidByBalance
] as const;
const CREDIT_TYPES = [InvoiceType.CreditNote, InvoiceType.PaymentVoucher] as const;
const REMITTANCE_STATUSES = [InvoiceStatus.ManualRemittance] as const;

function isRemittanceStatus(status: string | undefined): boolean {
  return (
    status !== undefined &&
    REMITTANCE_STATUSES.includes(status as typeof REMITTANCE_STATUSES[number])
  );
}

function isOverdue(dueDate: Date | string | undefined): boolean {
  if (!dueDate) return false;
  return new Date(dueDate) < new Date();
}

function isCreditType(type: string | undefined): boolean {
  return type !== undefined && CREDIT_TYPES.includes(type as typeof CREDIT_TYPES[number]);
}

export function isPaidStatus(status: string | undefined): boolean {
  return status !== undefined && PAID_STATUSES.includes(status as typeof PAID_STATUSES[number]);
}

function isPendingStatus(status: string | undefined): boolean {
  return (
    status !== undefined && PENDING_STATUSES.includes(status as typeof PENDING_STATUSES[number])
  );
}

function isCollectionStatus(status: string | undefined): boolean {
  return (
    status !== undefined &&
    COLLECTION_STATUSES.includes(status as typeof COLLECTION_STATUSES[number])
  );
}

function isCreditRevenueOperation(type: string | undefined): boolean {
  return type === RevenueOperationType.Credit;
}

function isDebitRevenueOperation(type: string | undefined): boolean {
  return type === RevenueOperationType.Debit;
}

function getDefaultDisplayInfo(cmsKey: string): InvoiceStatusDisplayInfo {
  return {
    cmsKey,
    variant: 'default',
    showBadge: false,
    rowClassName: ''
  };
}

function getPendingDisplayInfo(suffix: 'Business' | 'Private'): InvoiceStatusDisplayInfo {
  return getDefaultDisplayInfo(`InvoiceStatusPending${suffix}`);
}

function getPaidDisplayInfo(suffix: 'Business' | 'Private'): InvoiceStatusDisplayInfo {
  return getDefaultDisplayInfo(`InvoiceStatusPaid${suffix}`);
}

function getPaidOutDisplayInfo(suffix: 'Business' | 'Private'): InvoiceStatusDisplayInfo {
  return getDefaultDisplayInfo(`InvoiceStatusPaidOut${suffix}`);
}

function getUnpaidDisplayInfo(suffix: 'Business' | 'Private'): InvoiceStatusDisplayInfo {
  return getDefaultDisplayInfo(`InvoiceStatusUnpaid${suffix}`);
}

function getPrivateCreditUnpaidDisplayInfo(): InvoiceStatusDisplayInfo {
  return getDefaultDisplayInfo('InvoiceStatusUnpaidPrivateCredit');
}

function getOverdueDisplayInfo(suffix: 'Business' | 'Private'): InvoiceStatusDisplayInfo {
  return {
    cmsKey: `InvoiceStatusOverdue${suffix}`,
    variant: 'overdue',
    showBadge: false,
    rowClassName: ''
  };
}

function getCreditBalanceDisplayInfo(
  suffix: 'Business' | 'Private',
  context: InvoiceStatusContext
): InvoiceStatusDisplayInfo {
  return {
    cmsKey: `InvoiceStatusCreditBalance${suffix}`,
    variant: 'creditBalance',
    showBadge: context === 'private',
    rowClassName: context === 'private' ? 'credit-row' : ''
  };
}

function getPrivateMissingPaymentDisplayInfo(
  invoiceStatus: string | undefined,
  dueDate: Date | string | undefined
): InvoiceStatusDisplayInfo {
  if (invoiceStatus === InvoiceStatus.PaymentReminderSent) {
    return {
      cmsKey: isOverdue(dueDate)
        ? 'InvoiceStatusPaymentReminderOverduePrivate'
        : 'InvoiceStatusPaymentReminderPrivate',
      variant: 'missingPayment',
      showBadge: true,
      rowClassName: 'outstanding-row'
    };
  }

  return {
    cmsKey: 'InvoiceStatusMissingPaymentPrivate',
    variant: 'missingPayment',
    showBadge: true,
    rowClassName: 'outstanding-row'
  };
}

export function getInvoiceStatusDisplayInfo(
  params: {
    invoiceStatus?: string;
    invoiceType?: string;
    transactionType?: string;
    revenueOperationType?: string;
    dueDate?: Date | string;
  },
  context: InvoiceStatusContext
): InvoiceStatusDisplayInfo {
  const { invoiceStatus, invoiceType, transactionType, revenueOperationType, dueDate } = params;
  const suffix =
    context === 'business-invoice' || context === 'business-card' ? 'Business' : 'Private';
  const isPrivateContext = context === 'private';
  const isCreditTypeInvoice = isCreditType(invoiceType);
  const isCreditRevenue = isCreditRevenueOperation(revenueOperationType);
  const isDebitRevenue = isDebitRevenueOperation(revenueOperationType);
  const isCollection = isCollectionStatus(invoiceStatus);
  const isOverdueInvoice = isOverdue(dueDate);

  if (transactionType === TransactionType.Payment) {
    if (revenueOperationType === RevenueOperationType.Credit) {
      return {
        cmsKey: `InvoiceStatusPaidOutPayment${suffix}`,
        variant: 'paidOut',
        showBadge: context === 'private',
        rowClassName: context === 'private' ? 'outstanding-row' : ''
      };
    }
    return {
      cmsKey: `InvoiceStatusDeposited${suffix}`,
      variant: 'deposited',
      showBadge: context === 'private',
      rowClassName: context === 'private' ? 'credit-row' : ''
    };
  }

  if (!invoiceStatus && transactionType === TransactionType.Charge) {
    return getPendingDisplayInfo(suffix);
  }

  if (
    invoiceStatus === InvoiceStatus.Draft ||
    invoiceStatus === InvoiceStatus.AutomatedRemittance
  ) {
    return getPendingDisplayInfo(suffix);
  }

  if (isCreditTypeInvoice) {
    if (isPaidStatus(invoiceStatus)) {
      if (isCreditRevenue) {
        return getPaidDisplayInfo(suffix);
      }
      return getPaidOutDisplayInfo(suffix);
    }

    if (isRemittanceStatus(invoiceStatus) || isCollection) {
      if (isCreditRevenue) {
        return isPrivateContext
          ? getPrivateCreditUnpaidDisplayInfo()
          : getUnpaidDisplayInfo(suffix);
      }
      return getCreditBalanceDisplayInfo(suffix, context);
    }
  }

  if (isPaidStatus(invoiceStatus)) {
    if (isPrivateContext && isDebitRevenue) {
      return getPaidOutDisplayInfo(suffix);
    }
    return getPaidDisplayInfo(suffix);
  }

  if (
    invoiceStatus === InvoiceStatus.PaymentReminderSent ||
    isPendingStatus(invoiceStatus) ||
    isCollection
  ) {
    if (isPrivateContext && isDebitRevenue) {
      return getCreditBalanceDisplayInfo(suffix, context);
    }

    if (isPrivateContext && isCreditRevenue) {
      const shouldShowMissingPayment =
        invoiceStatus === InvoiceStatus.PaymentReminderSent ||
        isPendingStatus(invoiceStatus) ||
        isCollection ||
        isOverdueInvoice;

      return shouldShowMissingPayment
        ? getPrivateMissingPaymentDisplayInfo(invoiceStatus, dueDate)
        : getPrivateCreditUnpaidDisplayInfo();
    }

    if (isPrivateContext) {
      const shouldShowMissingPayment =
        invoiceStatus === InvoiceStatus.PaymentReminderSent || isCollection || isOverdueInvoice;

      return shouldShowMissingPayment
        ? getPrivateMissingPaymentDisplayInfo(invoiceStatus, dueDate)
        : getUnpaidDisplayInfo(suffix);
    }

    if (invoiceStatus === InvoiceStatus.Pending && !isOverdueInvoice) {
      return getUnpaidDisplayInfo(suffix);
    }

    if (invoiceStatus === InvoiceStatus.PaymentReminderSent && !isOverdueInvoice) {
      return getUnpaidDisplayInfo(suffix);
    }

    return getOverdueDisplayInfo(suffix);
  }

  if (invoiceStatus === 'CreditBalance' || (isRemittanceStatus(invoiceStatus) && !invoiceType)) {
    return getCreditBalanceDisplayInfo(suffix, context);
  }
  return getUnpaidDisplayInfo(suffix);
}

export function getTransactionDisplayAmount(
  params: {
    amount?: number;
    invoiceStatus?: string;
    invoiceType?: string;
    transactionType?: string;
    revenueOperationType?: string;
    dueDate?: Date | string;
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  context: InvoiceStatusContext
): number {
  return Math.abs(params.amount ?? 0);
}

export function getInvoiceTypeLinkCmsKey(
  invoiceType?: string,
  defaultKey: string = 'ViewInvoice'
): string {
  if (!invoiceType) return defaultKey;
  switch (invoiceType) {
    case InvoiceType.Invoice:
      return 'ViewInvoice';
    case 'Receipt':
    case 'CreditCard':
      return 'ViewReceipt';
    case InvoiceType.CreditNote:
    case InvoiceType.PaymentVoucher:
      return 'ViewCreditNote';
    default:
      return 'ViewInvoice';
  }
}

export type TransactionLinkRowContext = {
  invoiceStatus?: string;
  transactionType?: string;
  invoiceTypeLinkDefault?: string;
  /** Required for the link-text rules below (business-card always shows receipt link, private uses invoice-number presence). */
  context?: InvoiceStatusContext;
  /** Presence of an invoice number on the row; used in private context to tell card payments from invoice payments. */
  hasInvoiceNumber?: boolean;
};

export function getTransactionLinkDisplayInfo(
  statusInfo: InvoiceStatusDisplayInfo,
  invoiceType?: string,
  linkRowContext?: TransactionLinkRowContext
): { showLink: boolean; cmsKey: string } {
  if (
    linkRowContext?.invoiceStatus === InvoiceStatus.Draft ||
    linkRowContext?.invoiceStatus === InvoiceStatus.AutomatedRemittance ||
    linkRowContext?.transactionType === TransactionType.Payment
  ) {
    return { showLink: false, cmsKey: 'ViewInvoice' };
  }

  const ctx = linkRowContext?.context;

  if (ctx === 'business-card') {
    return { showLink: true, cmsKey: 'ViewReceipt' };
  }

  if (ctx === 'private' && linkRowContext?.hasInvoiceNumber === false) {
    return { showLink: true, cmsKey: 'ViewReceipt' };
  }

  return {
    showLink: true,
    cmsKey: getInvoiceTypeLinkCmsKey(
      invoiceType,
      linkRowContext?.invoiceTypeLinkDefault ?? 'ViewInvoice'
    )
  };
}
